import React from "react";

import type { ConsentRole } from "./recordingStatus";
import type { RecordingConsentState } from "./useRecordingConsent";

/**
 * RecordingConsentBanner - the in-room prompt for the D-05 consent question and the D-09
 * recording state, used by the default room (VideoRoomPage.tsx).
 *
 * It renders only what the server already validated: `consent.ui` is the single mapping from the
 * server's recording status to a message and the actions this participant may take, so the banner
 * never decides on its own that capture is on (T-06.1-45, D-09). Every string is a React text
 * child, so server-provided text can never become markup (T-06.1-47).
 *
 * A participant whose token withholds publishing (D-06) is told why their camera and microphone
 * are not on yet. This component does not turn them on: useRecordingConsent does that once the
 * server grants publish, because the SFU, not this banner, owns the real rule.
 */
export function RecordingConsentBanner(p: {
  consent: RecordingConsentState;
  /** Chooses the accept wording: the worker records the call, the customer agrees to it. */
  role: ConsentRole;
}) {
  const { consent, role } = p;
  const { ui, deciding, error, publishHeld } = consent;

  if (ui.kind === "hidden") {
    return null;
  }

  // The primary action is always an acceptance; the secondary is always a refusal ("Don't
  // record" before an answer, "Stop recording" once capture is requested). Both map onto
  // consent.decide with the boolean the server expects.
  let primaryLabel: string | null = null;
  let secondaryLabel: string | null = null;

  switch (ui.kind) {
    case "ask":
      primaryLabel = role === "worker" ? "Record" : "I agree";
      secondaryLabel = "Don't record";
      break;
    case "declined-by-me":
      primaryLabel = "Allow recording";
      break;
    case "waiting-for-others":
    case "starting":
    case "recording":
      if (ui.canDecline) {
        secondaryLabel = "Stop recording";
      }
      break;
    default:
      break;
  }

  return (
    <div
      className="video-room-consent"
      role="region"
      aria-label="Recording"
      aria-live="polite"
    >
      <div className="video-room-consent-message">
        {ui.capturing && <span className="video-room-recording-dot" />}
        <span>{ui.message}</span>
      </div>
      {publishHeld && (
        <p className="video-room-consent-held">
          Your camera and microphone turn on after you answer.
        </p>
      )}
      {error != null && (
        <p className="video-room-consent-error" role="alert">
          {error}
        </p>
      )}
      {(primaryLabel != null || secondaryLabel != null) && (
        <div className="video-room-consent-actions">
          {primaryLabel != null && (
            <button
              className="video-room-consent-button"
              disabled={deciding}
              onClick={() => consent.decide(true)}
            >
              {primaryLabel}
            </button>
          )}
          {secondaryLabel != null && (
            <button
              className="video-room-consent-button video-room-consent-button-secondary"
              disabled={deciding}
              onClick={() => consent.decide(false)}
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
